// src/hooks/useLicenseStatus.js
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/AuthContext';
import { useBranch } from './useBranches';

/**
 * Hook to check if user's current branch is blocked due to expired license
 * A branch is blocked when:
 * 1. subscription_status = 'expired' (trial ended without payment)
 * 2. child count > 10 (branches ≤10 children get freemium automatically)
 */
export const useLicenseStatus = () => {
  const { userProfile, isAuthenticated } = useAuth();

  // Get user's current branch ID
  const branchId = userProfile?.current_branch_id || userProfile?.branch_ids?.[0];

  // Check if user is admin (only admins need billing data)
  const isAdmin = userProfile?.active_role === 'branch_admin' ||
                  userProfile?.primary_role === 'branch_admin' ||
                  userProfile?.active_role === 'super_admin' ||
                  userProfile?.primary_role === 'super_admin';

  // Fetch branch details
  const { data: branch, isLoading: branchLoading } = useBranch(branchId, {
    enabled: !!branchId && isAuthenticated
  });

  // Fetch child count using RPC function
  const { data: childCountData, isLoading: childCountLoading } = useQuery({
    queryKey: ['branch-child-count', branchId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_branch_child_count', {
        p_branch_id: branchId
      });
      if (error) throw error;
      return data;
    },
    enabled: !!branchId && isAuthenticated
  });

  // Fetch billing data for additional info (only for admins - parents don't have RLS access)
  const { data: billing, isLoading: billingLoading } = useQuery({
    queryKey: ['branch-billing', branchId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('branch_billing')
        .select('*')
        .eq('branch_id', branchId)
        .single();
      if (error && error.code !== 'PGRST116') throw error; // Ignore "no rows" error
      return data;
    },
    enabled: !!branchId && isAuthenticated && isAdmin
  });

  const childCount = childCountData ?? 0;
  const isLoading = branchLoading || childCountLoading || (isAdmin && billingLoading);

  // Determine if blocked
  // Blocked when: subscription_status is 'expired' AND child count > 10
  // NEVER blocked if branch has permanent free license
  const isBlocked =
    !branch?.is_permanent_free &&
    branch?.subscription_status === 'expired' &&
    childCount > 10;

  // Check if branch has permanent free license
  const isPermanentFree = branch?.is_permanent_free === true;

  // Calculate monthly price for display (same formula as Billing.jsx)
  const calculateMonthlyPrice = () => {
    // Permanent free license = no charge
    if (isPermanentFree) return 0;
    if (billing?.is_grandfathered && billing?.grandfathered_price_cents) {
      return billing.grandfathered_price_cents / 100;
    }
    if (childCount <= 10) return 0;
    return Math.min(childCount * 4, 150);
  };

  return {
    isBlocked,
    isLoading,
    branch,
    billing,
    childCount,
    billingType: branch?.billing_type,
    subscriptionStatus: branch?.subscription_status,
    monthlyPrice: calculateMonthlyPrice(),
    branchId,
    isPermanentFree
  };
};

export default useLicenseStatus;
